import { Box, Button, Container, Flex, Heading, useColorMode } from "theme-ui";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { getUserProjectManagerWorkerData } from "../../../../../store/actions/dashboardAction";
import { useQueryParams } from "../../Helper/customHook";
import ActiveProjectsPMChart from "./ActiveProjectsPMChart";
import TeamWorkloadChart from "./TeamWorkloadChart";
import ActiveProjectsSalesRepChart from "./ ActiveProjectsSalesRepChart";

const RealTimeWorkloadDashboard = () => {
    const [colorMode] = useColorMode();
    const bgDashboard = colorMode === 'light' ? "#ffffff" : "#2e354db8";
    const titleHeader = colorMode === 'light' ? "#453521" : "#dadada";

    const dispatch = useDispatch();
    const { UserTaskManagerWorkerData, UserTaskManagerWorkerLoading, UserSalesRepresentativeData, chartLoading } = useSelector((state: any) => ({
        UserTaskManagerWorkerData: state.dashboardData.UserTaskManagerWorkerData,
        UserTaskManagerWorkerLoading: state.dashboardData.UserTaskManagerWorkerLoading,
        UserSalesRepresentativeData: state.dashboardData.UserSalesRepresentativeData,
        chartLoading: state.dashboardData.chartLoading
    }), shallowEqual);
    const { createUrl, searchParams, setMultipleParams, removeParam } = useQueryParams();
    const currentFlag = searchParams.get("flag") || "real-time-workload-all-data";
    const currentChartKey = searchParams.get("chartKey") || "";
    const isFirstMount = useRef(true);

    const isOnReload = useMemo(() => {
        try {
            const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
            return navigation ? navigation.type === 'reload' : false;
        } catch (e) {
            return false;
        }
    }, []);

    const [refreshKey, setRefreshKey] = useState(0);
    const FIXED_TEAMS = ['Mechanical', 'Electrical', 'Plumbing', "Structural"];

    const allTeams = useMemo(() => {
        const dataKeys = Object.keys(UserTaskManagerWorkerData || {});
        return Array.from(new Set([...FIXED_TEAMS, ...dataKeys]));
    }, [UserTaskManagerWorkerData]);

    useEffect(() => {
        const isInitial = isFirstMount.current;
        if (isFirstMount.current) isFirstMount.current = false;

        const paramsForApi = new URLSearchParams(searchParams);
        paramsForApi.set("flag", currentFlag);

        const reloadValue = isInitial;
        paramsForApi.set("isOnReload", String(reloadValue));

        let currentTeamVal = "all-team";
        if (currentChartKey === "pm") {
            currentTeamVal = "projectManager";
        } else if (currentChartKey === "salesRepresentative") {
            currentTeamVal = "salesRepresentative";
        } else if (currentChartKey?.startsWith("team_")) {
            currentTeamVal = currentChartKey.replace("team_", "").toLowerCase();
        }

        paramsForApi.set("currentTeam", currentTeamVal);

        // On-Demand Data: If a specific section is focused (and not a global reload), strip other sections' dates
        if (!reloadValue && currentTeamVal !== "all-team") {
            const dateSuffixes = ['_start', '_end'];
            const activePrefix = `${currentTeamVal}_`;

            Array.from(paramsForApi.keys()).forEach(key => {
                if (dateSuffixes.some(suffix => key.endsWith(suffix))) {
                    if (!key.startsWith(activePrefix)) {
                        paramsForApi.delete(key);
                    }
                }
            });
        }

        // If it's a reload initial mount, trigger global loading
        const effectiveChartKey = reloadValue ? "" : currentChartKey;
        dispatch(getUserProjectManagerWorkerData(paramsForApi.toString(), effectiveChartKey));
    }, [searchParams, currentFlag, currentChartKey, isOnReload]);

    const handleClearFilters = useCallback(() => {
        const allParams = Array.from(searchParams.keys());
        removeParam(allParams);
        setRefreshKey(prev => prev + 1);
    }, [removeParam, searchParams]);

    const handlePMDateFilter = useCallback((range: string) => {
        if (range && !range.includes(" to ")) return;

        if (range) {
            const [start, end] = range.split(" to ");
            setMultipleParams({
                projectManager_start: start,
                projectManager_end: end,
                flag: "real-time-workload-project-manager-worker-data",
                chartKey: "pm"
            });
        } else {
            setMultipleParams({
                projectManager_start: null,
                projectManager_end: null,
                chartKey: "pm"
            });
        }
    }, [setMultipleParams]);

    const handleSalesRepDateFilter = useCallback((range: string) => {
        if (range && !range.includes(" to ")) return;

        if (range) {
            const [start, end] = range.split(" to ");
            setMultipleParams({
                salesRepresentative_start: start,
                salesRepresentative_end: end,
                flag: "real-time-workload-sales-representative-data",
                chartKey: "salesRepresentative"
            });
        } else {
            setMultipleParams({
                salesRepresentative_start: null,
                salesRepresentative_end: null,
                chartKey: "salesRepresentative"
            });
        }
    }, [setMultipleParams]);

    const handleTeamDateFilter = useCallback((teamName: string, range: string, viewMode: 'worker' | 'manager') => {
        if (range && !range.includes(" to ")) return;
        const lowerTeam = teamName.toLowerCase();

        if (range) {
            const [start, end] = range.split(" to ");
            setMultipleParams({
                [`${lowerTeam}_start`]: start,
                [`${lowerTeam}_end`]: end,
                flag: "real-time-workload-task-manager-worker-data",
                chartKey: `team_${teamName}`,
                teamName: teamName,
                viewMode: viewMode
            });
        } else {
            setMultipleParams({
                [`${lowerTeam}_start`]: null,
                [`${lowerTeam}_end`]: null,
                chartKey: `team_${teamName}`,
                teamName: teamName,
                viewMode: viewMode
            });
        }
    }, [setMultipleParams]);


    return (
        <div className="main-content" style={{ minHeight: "100vh", overflow: "scroll" }}>
            <Box sx={{ bg: bgDashboard, fontFamily: "poppins" }}>
                <Box>
                    <Box
                        sx={{
                            width: "100%",
                            background: bgDashboard,
                            p: "20px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: ["flex-start", "flex-start", "flex-start", "center"],
                            flexDirection: ["column", "column", "column", "row"],
                        }}
                    >
                        <Flex sx={{ width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Heading as="h2" sx={{ fontSize: [4, 5], color: titleHeader }}>
                                Real Time Workload
                            </Heading>
                            <Button
                                onClick={handleClearFilters}
                                sx={{
                                    bg: 'transparent',
                                    color: 'primary',
                                    border: '1px solid',
                                    borderColor: 'primary',
                                    px: 3,
                                    py: 1,
                                    fontSize: 1,
                                    cursor: 'pointer',
                                    borderRadius: 4,
                                    '&:hover': { bg: 'primary', color: 'white' }
                                }}
                            >
                                Clear All Filters
                            </Button>
                        </Flex>
                    </Box>
                </Box>
            </Box>
            <Container sx={{ padding: "20px", backgroundColor: colorMode === 'light' ? "#FFFBF5" : "#20283c", display: "flex", flexDirection: "column", gap: "20px" }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {/* PM Chart */}
                    <ActiveProjectsPMChart
                        key={`pm_${refreshKey}`}
                        dateRange={searchParams.get("projectManager_start") && searchParams.get("projectManager_end") ? `${searchParams.get("projectManager_start")} to ${searchParams.get("projectManager_end")}` : ""}
                        setDateRange={handlePMDateFilter}
                    />

                    {/* Team Charts with Independant Toggles */}
                    {allTeams.map((teamName) => {
                        const lowerTeam = teamName.toLowerCase();
                        const teamStartDate = searchParams.get(`${lowerTeam}_start`);
                        const teamEndDate = searchParams.get(`${lowerTeam}_end`);
                        const timeModeParam = searchParams.get(`${teamName}_timeMode`) as 'remaining' | 'estimation' || 'remaining';

                        return (
                            <TeamWorkloadChart
                                key={`${teamName}_${refreshKey}`}
                                teamName={teamName}
                                teamColorSet={teamName.toLowerCase()}
                                teamData={UserTaskManagerWorkerData?.[teamName] || { workers: [], managers: [] }}
                                dateRange={teamStartDate && teamEndDate ? `${teamStartDate} to ${teamEndDate}` : ""}
                                setDateRange={(val: string, viewMode: 'worker' | 'manager') => handleTeamDateFilter(teamName, val, viewMode)}
                                timeMode={timeModeParam}
                                setTimeMode={(mode: 'remaining' | 'estimation') => {
                                    setMultipleParams({
                                        [`${teamName}_timeMode`]: mode,
                                        isRemainingTime: mode === 'remaining' ? 'true' : 'false',
                                        isEstimationTime: mode === 'estimation' ? 'true' : 'false',
                                        flag: "real-time-workload-task-manager-worker-data",
                                        chartKey: `team_${teamName}`
                                    });
                                }}
                                loading={UserTaskManagerWorkerLoading || chartLoading['all'] || chartLoading[`team_${teamName}`] || chartLoading[`team_${teamName}_worker`] || chartLoading[`team_${teamName}_manager`]}
                            />
                        );
                    })}
                    {/* Sales Representative Chart */}
                    <ActiveProjectsSalesRepChart
                        key={`salesRep_${refreshKey}`}
                        dateRange={searchParams.get("salesRepresentative_start") && searchParams.get("salesRepresentative_end") ? `${searchParams.get("salesRepresentative_start")} to ${searchParams.get("salesRepresentative_end")}` : ""}
                        setDateRange={handleSalesRepDateFilter}
                    />
                </Box>
            </Container >
        </div >
    )
}

export default RealTimeWorkloadDashboard